/**
 * Location options for every calendar-hosted scheduling surface (issues #503,
 * #513, #514): the Contact Details Schedule tab, the Calendar page's
 * calendar editor (pages/calendar/CalendarEditorPage) and the booking-link editor.
 *
 * A conference option is offered only when the SELECTED calendar can MINT it:
 *   Google Calendar  → Google Meet      (created server-side by Google on insert)
 *   Outlook Calendar → Microsoft Teams  (created by Graph on insert, #514)
 *
 * Neither provider can mint the other's link, so the two branches below are
 * mutually exclusive by design, not by convenience. The backend enforces the
 * same rule (400 on a cross-provider combination) — this module is the ONE
 * frontend copy of it; do not re-derive it per surface.
 *
 * Every surface ALSO offers a way out of the conference option: a calendar
 * event with no conference resource. Without it a Microsoft account whose
 * licence disallows Teams had no reachable path at all — the Outlook list had
 * exactly one entry, so every create forced `add_teams_meeting: true` and hit
 * the backend's 422, whose copy ("choose a different location") named an
 * option the UI did not offer (#514 audit A03).
 *
 * Zoom was RETIRED on 2026-09-04 (#513) — routes, tokens and UI are gone.
 * There is deliberately no flag for it.
 */

// Phone and In person were WITHDRAWN on 2026-09-04 (issue #513) while the
// Schedule flow is rebuilt around calendar-hosted invitations. They are not
// deleted: flipping this to `true` restores both options in one line, with
// their previous labels and payload strings ('Phone call', 'In person').
// Revert condition (recorded on #513): the wider Schedule rework lands, or
// product decides non-conference locations belong in the Schedule tab again.
// Precedent for a named lifetime flag: DESKTOP_APP_BETA in backend/server.py.
export const WITHDRAWN_LOCATIONS_RESTORED = false;

/** What the calendar event's `location` string reads for each option. */
export const SCHEDULE_LOCATION_LABELS = {
    google_meet: 'Google Meet',
    teams: 'Microsoft Teams',
    phone: 'Phone call',
    in_person: 'In person',
    // 'none' is "a calendar event with no conference resource", so the event's
    // location string is EMPTY — the backend stores `location` verbatim on the
    // CRM stub, and "No conference link" is picker copy, not event copy.
    none: '',
};

/**
 * The ONE provider rule: which conference link each calendar can mint.
 * Everything else in this module (and every surface that imports it) is
 * derived from this map — never re-list the pairs.
 */
const CONFERENCE_OPTION = {
    google: { value: 'google_meet', label: 'Google Meet (auto-link)' },
    outlook: { value: 'teams', label: 'Microsoft Teams (auto-link)' },
};

/** The option every calendar offers: an event with no conference resource. */
const NO_CONFERENCE_OPTION = { value: 'none', label: 'No conference link' };

/** Location values whose event/booking mints a conference link. */
export const CONFERENCE_LOCATIONS = Object.values(CONFERENCE_OPTION).map((o) => o.value);

/**
 * True when this location value means "a conference link is minted for it".
 * Drives the Video-vs-MapPin badges on the booking-link list and the public
 * booking page, which each used to hand-declare their own copy of the pair.
 * @param {string|null|undefined} location
 */
export function isConferenceLocation(location) {
    return CONFERENCE_LOCATIONS.includes(location);
}

/**
 * The conference option(s) a calendar choice can mint — a FRESH array so
 * callers may append to it.
 * @param {'google'|'outlook'|null|undefined} selectedCalendar
 */
function conferenceOptionsFor(selectedCalendar) {
    const opt = Object.prototype.hasOwnProperty.call(CONFERENCE_OPTION, selectedCalendar)
        ? CONFERENCE_OPTION[selectedCalendar]
        : null;
    return opt ? [opt] : [];
}

/**
 * @param {'google'|'outlook'|null|undefined} selectedCalendar
 * @param {{restoreWithdrawn?: boolean}} [opts] test seam; defaults to the constant
 * @returns {{value: string, label: string}[]} ordered options; [] when none
 */
export function buildLocationOptions(selectedCalendar, { restoreWithdrawn = WITHDRAWN_LOCATIONS_RESTORED } = {}) {
    const opts = conferenceOptionsFor(selectedCalendar);
    // A03 (#514 audit): ONE append, both providers — symmetric on purpose.
    // A user may legitimately want a calendar event with no conference
    // resource, and for an unlicensed Microsoft user it is the only way to
    // schedule to Outlook at all. Guarded only so "no calendar selected yet"
    // still means "no options" (the pickers' documented 0-option state).
    if (opts.length > 0) {
        opts.push(NO_CONFERENCE_OPTION);
    }
    if (restoreWithdrawn) {
        opts.push({ value: 'phone', label: 'Phone' });
        opts.push({ value: 'in_person', label: 'In person' });
    }
    return opts;
}

/**
 * The create payload's two conference flags, derived from the (calendar,
 * location) pair — the backend 400s a cross-provider combination (#514). The
 * rule was written out in both scheduling modals; it lives here now, next to
 * the provider map it follows (#680).
 * @param {'google'|'outlook'|null|undefined} calendar
 * @param {string|undefined} location - an option value from buildLocationOptions
 * @returns {{add_google_meet: boolean, add_teams_meeting: boolean}}
 */
export function conferenceFlags(calendar, location) {
    return {
        add_google_meet: calendar === 'google' && location === 'google_meet',
        add_teams_meeting: calendar === 'outlook' && location === 'teams',
    };
}

/** The booking-link tail — locations that need no minted conference link. */
export const NON_CONFERENCE_LINK_LOCATIONS = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'in_person', label: 'In Person' },
    { value: 'custom', label: 'Custom' },
];

/**
 * The booking-link editor's location list. Same provider rule, different tail:
 * a booking page can be a phone call, an in-person meeting or a custom
 * arrangement, none of which need a minted link.
 *
 * `calendar` is '' / null when the link defers the choice ("auto" — the host
 * is resolved at booking time from the assignee's connected calendars, see
 * routes/booking.py::_resolve_booking_calendar). Both conference options stay
 * on offer then, because either provider might end up hosting.
 *
 * Deliberately NOT `[...buildLocationOptions(calendar), ...tail]`: the tail
 * already carries 'custom', so inheriting the Schedule tab's 'none' would put
 * two ways to say "no conference link" in one list.
 *
 * @param {'google'|'outlook'|''|null|undefined} calendar
 */
export function buildBookingLinkLocationOptions(calendar) {
    const conference = calendar
        ? conferenceOptionsFor(calendar)
        : [CONFERENCE_OPTION.google, CONFERENCE_OPTION.outlook];
    return [...conference, ...NON_CONFERENCE_LINK_LOCATIONS];
}

/** Event `location` payload string; '' when nothing is selected. */
export function locationLabel(value) {
    if (!value) return '';
    return SCHEDULE_LOCATION_LABELS[value] ?? value;
}

/** Copy for the 0-option state. `calendarLabel` is the selected calendar's label. */
export function emptyLocationCopy(calendarLabel) {
    // Since #514 both providers have a conference option, so this state is
    // reached only while the connected set is still loading (selectedCalendar
    // is null) — never because a connected calendar lacks an option. Kept
    // because the modals render before that resolves.
    return `No conference option is available for ${calendarLabel || 'this calendar'} yet. ` +
        'The invitation will still be sent from that calendar; add a meeting link to the ' +
        'invitation message if you need one.';
}

/**
 * Label for the "join" affordance a booking confirmation renders next to
 * `meet_link` (which carries the Teams join URL too — the field name predates
 * #514 and is deliberately unchanged). One helper so the public booking page,
 * the funnel BookingWidget and the public reschedule page never each carry
 * their own copy of the ternary.
 */
export function joinButtonLabel(location) {
    if (location === 'google_meet') return 'Join Google Meet';
    if (location === 'teams') return 'Join Microsoft Teams';
    return 'Join Meeting';
}
